<template>
  <div>
    <div class="grid grid-cols-12 gap-6">
      <div class="col-span-12 lg:col-span-5">
        <ChartCard title="เบี้ยยังชีพผู้สูงอายุ">
          <div class="h-[400px]">
            <ChartWithLegend
              chart-id="peAllowanceChart"
              :series="allowance.series"
              :labels="allowance.labels"
              unit="คน"
              :height="200"
              :show-data-label="true"
              :palette="allowancePalette"
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-7">
        <ChartCard title="โรคประจำตัว">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="peDiseaseChart"
              type="bar"
              :data="diseaseData"
              :options="navyBarOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ChartCard from "../../container/ChartCard.vue";
import ChartWithLegend from "../../container/ChartWithLegend.vue";
import DashboardBarEChart from "../../container/DashboardBarEChart.vue";
import { globalNumDecFixed } from "@/utils/format-number.js";

const props = defineProps({
  data: { type: Object, default: () => ({}) },
});

const P = { navy: "#3A506A", blue: "#20A0D7", yellow: "#F4D35E", green: "#11D67A", red: "#F73C4B", orange: "#FE9000", gray: "#606468" };

const ALLOWANCE_STATUS_MAP = {
  "ได้รับ": "received",
  "สมัคร แต่อยู่ระหว่างการรอรับสิทธิ": "pending",
  "ไม่ได้สมัคร เพราะไม่รู้ข้อมูลว่ามีโครงการนี้": "unaware",
  "ไม่ได้สมัคร เพราะไม่เข้าเกณฑ์หรือไม่ต้องการได้รับเงินนี้แต่แรก": "ineligible",
};
const ALLOWANCE_DISPLAY_BY_KEY = {
  received: "ได้รับ",
  pending: "สมัคร แต่รอรับสิทธิ",
  unaware: "ไม่ได้สมัคร เพราะไม่รู้ข้อมูล",
  ineligible: "ไม่ได้สมัคร เพราะไม่เข้าเกณฑ์/ไม่ต้องการ",
};
const ALLOWANCE_COLOR_BY_KEY = {
  received: P.green,
  pending: P.blue,
  unaware: P.red,
  ineligible: P.yellow,
};

const bar = (label, color, data, opts = {}) => ({
  label, backgroundColor: color, data, barPercentage: 0.6, categoryPercentage: 0.8, ...opts,
});

const barOptions = ({ legend = false, stacked = false, horizontal = false, aspectRatio = 2 } = {}) => {
  const cat = { stacked, grid: { display: false }, ticks: { font: { size: 12 }, autoSkip: false, maxRotation: horizontal ? 0 : 45, minRotation: horizontal ? 0 : 45 } };
  const val = { stacked, beginAtZero: true, grid: { color: "#E2E8F0" }, ticks: { font: { size: 12 }, precision: 0 } };
  return {
    indexAxis: horizontal ? "y" : "x",
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio,
    plugins: {
      legend: { display: legend, position: "bottom", labels: { usePointStyle: true, boxWidth: 8, font: { size: 12 } } },
      tooltip: {
        enabled: true,
        callbacks: {
          label(ctx) {
            const numVal = horizontal ? ctx.parsed.x : ctx.parsed.y;
            return `${ctx.dataset.label}: ${globalNumDecFixed(numVal)} คน`;
          },
        },
      },
    },
    scales: horizontal ? { x: val, y: cat } : { x: cat, y: val },
  };
};

const toNumber = (value) => Number(value || 0);

const allowance = computed(() => {
  const items = props.data?.elderly_allowance || [];
  if (items.length) {
    return {
      labels: items.map((item) => {
        const key = ALLOWANCE_STATUS_MAP[item.name || ""];
        return key ? ALLOWANCE_DISPLAY_BY_KEY[key] : (item.name || "");
      }),
      series: items.map((item) => toNumber(item.total)),
    };
  }
  return {
    labels: [],
    series: [],
  }
});
const allowancePalette = computed(() =>
  (props.data?.elderly_allowance || []).map((item) => {
    const key = ALLOWANCE_STATUS_MAP[item.name || ""];
    return key ? ALLOWANCE_COLOR_BY_KEY[key] : P.gray;
  })
);

const diseaseData = computed(() => {
  const items = props.data?.underlying_disease || [];
  const diseaseDisplay = [
    { key: "d1", display: ["ไม่มีโรคประจำตัว"] },
    { key: "d2", display: ["กลุ่ม NCDs"] },
    { key: "d3", display: ["กลุ่มโรคเกี่ยวกับอวัยวะ", "และระบบสำคัญ"] },
    { key: "d4", display: ["กลุ่มการบาดเจ็บร้ายแรง", "และทุพพลภาพ"] },
    { key: "d5", display: ["กลุ่มโรคจิตเวช"] },
    { key: "d6", display: ["โรคอื่น ๆ"] },
  ];
  const diseaseMap = {
    "ไม่มีโรคประจำตัว": "d1",
    "กลุ่ม NCDs": "d2",
    "กลุ่มโรคเกี่ยวกับอวัยวะและระบบสำคัญ": "d3",
    "กลุ่มการบาดเจ็บร้ายแรงและทุพพลภาพ": "d4",
    "กลุ่มโรคจิตเวช": "d5",
    "โรคอื่น ๆ": "d6",
    "โรคอื่นๆ": "d6"
  }
  if (items.length) {
    return {
      labels: diseaseDisplay.map((item) => item.display),
      datasets: [
        bar(
          "จำนวนคน",
          "#d6b170",
          diseaseDisplay.map((d) =>
            toNumber(
              items.find(
                (item) => diseaseMap[(item.name || "")] === d.key
              )?.total
            )
          ),
          { barPercentage: 0.4 }
        )
      ],
    };
  }
  return {
    labels: [],
    datasets: [],
  }
});

const navyBarOptions = barOptions({ aspectRatio: 1.8 });
</script>
